import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin, Clock, Snowflake, Wrench, CircleDot, ArrowRight, MoveHorizontal } from "lucide-react";
import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import Marquee from "../components/site/Marquee";
import PricingTable from "../components/site/PricingTable";
import { FormulaireDevis, useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import { GARAGE, IMAGES, TARIFS_DEFAUT } from "../lib/constants";
import { assetUrl } from "../lib/api";

const LIGNES_HERO = ["Pneus, climatisation", "et mécanique", "au Cannet"];

function Hero({ heroPhoto }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const { openDevis } = useDevis();

  return (
    <section ref={ref} className="relative bg-brand-dark overflow-hidden max-h-[70vh] sm:max-h-none" data-testid="hero-accueil">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={assetUrl(heroPhoto || IMAGES.hero)}
          alt="Accueil de l'atelier Promo Pneus au Cannet, comptoir et fresque"
          className="w-full h-[120%] object-cover opacity-40"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/40 to-brand-dark/60" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-14 sm:pt-28 sm:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base sm:text-lg mb-6"
        >
          {"//"} Garage indépendant — Le Cannet
        </motion.p>

        <h1 className="font-heading font-bold uppercase leading-[0.95] tracking-wide text-4xl sm:text-6xl md:text-7xl text-white max-w-4xl">
          {LIGNES_HERO.map((ligne, i) => (
            <span key={i} className="reveal-line">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? <span className="text-brand-red">{ligne}</span> : ligne}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-6 text-lg sm:text-xl text-white/85 font-body max-w-xl"
        >
          Sans rendez-vous, du lundi au samedi.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.6 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8"
        >
          <button
            data-testid="hero-demander-devis"
            onClick={() => openDevis()}
            className="bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 text-center transition-colors w-full sm:w-auto"
          >
            Demander un devis
          </button>
          <a
            href="#tarifs-apercu"
            data-testid="hero-voir-tarifs"
            className="text-white/85 hover:text-white font-heading font-semibold uppercase tracking-wider text-base underline underline-offset-8 decoration-brand-red decoration-2 transition-colors text-center sm:text-left"
          >
            Voir nos tarifs
          </a>
        </motion.div>
      </div>
    </section>
  );
}

const SERVICES = [
  {
    titre: "Pneumatique",
    lien: "/pneumatique",
    icone: CircleDot,
    texte: "Montage, équilibrage, réparation de crevaison. Pneus neufs et occasions, toutes dimensions, commande sous 24 à 48 h.",
    prix: "à partir de 20 € / pneu",
  },
  {
    titre: "Parallélisme",
    lien: "/parallelisme",
    icone: MoveHorizontal,
    texte: "Réglage de la géométrie des roues : train avant et 4 roues. Voiture qui tire, pneus usés d'un seul côté ? On contrôle.",
    prix: "Nous consulter",
  },
  {
    titre: "Climatisation",
    lien: "/climatisation",
    icone: Snowflake,
    texte: "Recharge de climatisation avec contrôle complet du circuit. Air froid l'été, désembuage rapide l'hiver.",
    prix: "à partir de 50 €",
  },
  {
    titre: "Mécanique",
    lien: "/mecanique",
    icone: Wrench,
    texte: "Vidange, révision, freinage, diagnostic électronique. Toutes marques, devis avant toute réparation.",
    prix: "à partir de 40 €",
  },
];

export default function Accueil() {
  const config = useConfig();
  const tarifs = config?.tarifs || TARIFS_DEFAUT;
  const promos = config?.promotions || [];

  return (
    <>
      <SEO
        title="Promo Pneus — Pneus, climatisation et mécanique au Cannet"
        description="Garage Promo Pneus au Cannet : montage de pneus dès 20 €, recharge de climatisation dès 50 €, mécanique toutes marques. Sans rendez-vous, près de Cannes."
      />
      <Hero heroPhoto={config?.hero_photo} />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Nos services
            </p>
            <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl text-brand-dark tracking-tight mb-12">
              Quatre métiers, un seul atelier
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.titre} delay={i * 0.12}>
                <Link
                  to={s.lien}
                  data-testid={`carte-service-${s.titre.toLowerCase()}`}
                  className="bg-white border border-gray-200 p-8 flex flex-col h-full group hover:border-brand-red transition-colors relative overflow-hidden"
                >
                  <span className="absolute top-0 right-0 font-heading font-bold text-7xl text-brand-light select-none leading-none p-4" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <s.icone size={40} className="text-brand-red mb-6" aria-hidden="true" />
                  <h3 className="font-heading font-semibold uppercase text-2xl sm:text-3xl text-brand-dark mb-3">
                    {s.titre}
                  </h3>
                  <p className="text-brand-body leading-relaxed mb-6">{s.texte}</p>
                  <p className="font-heading font-bold uppercase text-brand-red text-xl mt-auto mb-4">
                    {s.prix}
                  </p>
                  <span className="inline-flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-brand-dark group-hover:text-brand-red group-hover:gap-3 transition-all">
                    Voir les tarifs <ArrowRight size={18} aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <section id="tarifs-apercu" className="py-16 sm:py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Tarifs
            </p>
            <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl text-brand-dark tracking-tight mb-4">
              Les prix, tout de suite
            </h2>
            <p className="text-brand-body text-lg max-w-2xl mb-12">
              Pas de devis caché, pas de surprise à la caisse. Les prestations les plus demandées, avec leur prix d'appel.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              { service: "pneus", lien: "/pneumatique", label: "Pneumatique" },
              { service: "parallelisme", lien: "/parallelisme", label: "Parallélisme" },
              { service: "climatisation", lien: "/climatisation", label: "Climatisation" },
              { service: "mecanique", lien: "/mecanique", label: "Mécanique" },
            ].map((bloc, i) => (
              <Reveal key={bloc.service} delay={i * 0.1}>
                <div className="bg-brand-light p-6 h-full flex flex-col">
                  <h3 className="font-heading font-semibold uppercase text-2xl text-brand-navy mb-4">
                    {bloc.label}
                  </h3>
                  <ul className="space-y-3 mb-6">
                    {(tarifs[bloc.service]?.rows || []).slice(0, 3).map((r, j) => (
                      <li key={j} className="flex justify-between gap-3 border-b border-gray-200 pb-2">
                        <span className="text-brand-dark font-body">{r.prestation}</span>
                        <span className="font-heading font-semibold text-brand-red whitespace-nowrap">{r.prix}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={bloc.lien}
                    data-testid={`apercu-tarifs-${bloc.service}`}
                    className="mt-auto inline-flex items-center gap-2 font-heading font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-red transition-colors"
                  >
                    Tous les tarifs {bloc.label.toLowerCase()} <ArrowRight size={16} aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {promos.length > 0 && (
        <section className="py-16 sm:py-24 bg-brand-light" data-testid="section-promotions">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
                {"//"} En ce moment
              </p>
              <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl text-brand-dark tracking-tight mb-12">
                Promotions en cours
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {promos.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <article className="bg-white border-t-4 border-brand-red h-full flex flex-col" data-testid={`promo-${p.id}`}>
                    {p.photo && (
                      <img src={assetUrl(p.photo)} alt={`Illustration de la promotion : ${p.titre}`}
                        className="w-full h-44 object-cover" loading="lazy" />
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading font-semibold uppercase text-2xl text-brand-dark mb-2">{p.titre}</h3>
                      {p.prix && <p className="font-heading font-bold text-brand-red text-xl mb-3">{p.prix}</p>}
                      {p.description && <p className="text-brand-body leading-relaxed">{p.description}</p>}
                      <p className="mt-4 text-sm text-brand-body">
                        Valable jusqu'au {p.date_fin.split("-").reverse().join("/")} inclus.
                      </p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
                {"//"} Infos pratiques
              </p>
              <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl text-brand-dark tracking-tight mb-8">
                Passez quand vous voulez
              </h2>
              <ul className="space-y-5 mb-8">
                <li className="flex items-start gap-4">
                  <Clock size={24} className="text-brand-red mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-heading font-semibold uppercase text-brand-dark text-lg">Horaires</p>
                    <p className="text-brand-body">{GARAGE.horairesTexte}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={24} className="text-brand-red mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-heading font-semibold uppercase text-brand-dark text-lg">Adresse</p>
                    <p className="text-brand-body">{GARAGE.adresse}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={24} className="text-brand-red mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-heading font-semibold uppercase text-brand-dark text-lg">Téléphone</p>
                    <a href={`tel:${GARAGE.telephoneLien}`} className="text-brand-red font-semibold hover:text-brand-redhover transition-colors">
                      {GARAGE.telephone}
                    </a>
                  </div>
                </li>
              </ul>
              <img src={IMAGES.devanture} alt="Devanture du garage Promo Pneus, 40 chemin du Périer au Cannet"
                className="w-full h-64 object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={0.15}>
              <iframe
                title="Carte : Promo Pneus, 40 chemin du Périer, 06110 Le Cannet"
                src={GARAGE.mapsEmbed}
                className="w-full h-[420px] lg:h-full min-h-[420px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-brand-dark">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Par écrit
            </p>
            <h2 className="font-heading font-bold uppercase text-4xl sm:text-5xl text-white tracking-tight mb-4">
              Demandez votre devis
            </h2>
            <p className="text-white/70 text-lg mb-10">
              Le téléphone sonne souvent dans le vide en journée — l'atelier avant tout. Écrivez-nous :
              votre demande est lue chaque soir et nous vous rappelons sous 24 h ouvrées.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white p-6 sm:p-8">
              <FormulaireDevis />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
